import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Send, Check, Home, Building2 } from "lucide-react";

const contactSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Valid email required"),
  phone: z.string().min(10, "Valid phone number required"),
  projectType: z.string().min(1, "Please select project type"),
  homeType: z.string().optional(),
  locality: z.string().min(2, "Locality is required"),
  message: z.string().optional(),
});

const projectTypes = [
  { id: "b2c", label: "Homeowner", icon: Home },
  { id: "b2b-builder", label: "Builder", icon: Building2 },
  { id: "b2b-architect", label: "Architect", icon: Building2 },
];

const homeTypes = [
  { id: "2bhk", label: "2 BHK" },
  { id: "3bhk", label: "3 BHK" },
  { id: "4bhk", label: "4 BHK" },
  { id: "villa", label: "Villa" },
  { id: "commercial", label: "Commercial" },
];

export default function ContactForm({ defaultType = "b2c", onSuccess }) {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      projectType: defaultType,
    },
  });

  const watchProjectType = watch("projectType");

  const onSubmit = async (data) => {
    // Placeholder for API integration
    console.log("Contact form submitted:", data);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsSubmitted(true);
    if (onSuccess) {
      setTimeout(() => {
        onSuccess();
        setIsSubmitted(false);
        reset();
      }, 2000);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="font-heading text-2xl text-brand-primary mb-2">
          Thank you for reaching out!
        </h3>
        <p className="text-text-secondary">
          Our team will contact you within 24 hours to discuss your project.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="font-heading text-2xl text-brand-primary mb-6">
        Book Your Consultation
      </h3>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Project Type */}
        <div>
          <label className="form-label">I am a</label>
          <div className="grid grid-cols-3 gap-3">
            {projectTypes.map((type) => (
              <label
                key={type.id}
                className={`flex flex-col items-center p-3 rounded-xl border-2 cursor-pointer transition-all ${
                  watchProjectType === type.id
                    ? "border-brand-accent bg-brand-accent/5"
                    : "border-border-light hover:border-brand-accent/50"
                }`}
              >
                <input
                  {...register("projectType")}
                  type="radio"
                  value={type.id}
                  className="sr-only"
                />
                <type.icon
                  className={`w-5 h-5 mb-1 ${
                    watchProjectType === type.id
                      ? "text-brand-accent"
                      : "text-text-secondary"
                  }`}
                />
                <span className="text-sm font-medium text-brand-primary">
                  {type.label}
                </span>
              </label>
            ))}
          </div>
          {errors.projectType && (
            <p className="form-error">{errors.projectType.message}</p>
          )}
        </div>

        {/* Home Type - Only for B2C */}
        {watchProjectType === "b2c" && (
          <div>
            <label className="form-label">Home Type</label>
            <select {...register("homeType")} className="form-input">
              <option value="">Select home type</option>
              {homeTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Name */}
        <div>
          <label className="form-label">Full Name</label>
          <input
            {...register("name")}
            placeholder="Your name"
            className="form-input"
          />
          {errors.name && <p className="form-error">{errors.name.message}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="form-label">Email</label>
          <input
            {...register("email")}
            type="email"
            placeholder="your@email.com"
            className="form-input"
          />
          {errors.email && <p className="form-error">{errors.email.message}</p>}
        </div>

        {/* Phone */}
        <div>
          <label className="form-label">Phone / WhatsApp</label>
          <input
            {...register("phone")}
            placeholder="+91 799 565 9797"
            className="form-input"
          />
          {errors.phone && <p className="form-error">{errors.phone.message}</p>}
        </div>

        {/* Locality */}
        <div>
          <label className="form-label">Locality</label>
          <input
            {...register("locality")}
            placeholder="e.g., Gachibowli, Kondapur"
            className="form-input"
          />
          {errors.locality && (
            <p className="form-error">{errors.locality.message}</p>
          )}
        </div>

        {/* Message */}
        <div>
          <label className="form-label">
            Tell us about your project (optional)
          </label>
          <textarea
            {...register("message")}
            rows={4}
            placeholder="Share any specific requirements or questions..."
            className="form-input resize-none"
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full btn-primary disabled:opacity-50"
        >
          {isSubmitting ? (
            "Submitting..."
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Book Consultation
            </>
          )}
        </button>

        <p className="text-xs text-text-secondary text-center">
          By submitting, you agree to our privacy policy. We'll never spam you.
        </p>
      </form>
    </div>
  );
}
